import { useMutation } from "@pinia/colada";
import { Alert, CreateAlert, UpdateAlert, useApi } from "@soft-db/monitoring-api-client";
import { unref } from "vue";
import {
	composeCacheEffects,
	InvalidatorOptions,
	useInvalidator,
	useQueryEnabled,
	useScopeDataUpdater,
	useScopedQuery,
} from "./query";

// --- Keys ---

export const alertsKeys = {
	root: ["alerts"] as const,

	bySite: (siteId: string) => [...alertsKeys.root, "site", siteId] as const,

	byId: (alertId: string) => [...alertsKeys.root, "id", alertId] as const,

	notifications: (siteId: string, range: [Date, Date]) => [
		...alertsKeys.root,
		"notifications",
		siteId,
		{ start: range[0].toISOString(), end: range[1].toISOString() },
	],
};

// --- Cache effects ---

function useInvalidateSiteAlerts() {
	const invalidate = useInvalidator();

	return (alert: Alert) => {
		const opts: Required<InvalidatorOptions> = {
			keys: [],
			otherScopeKeys: [],
		};
		if (alert.site != null) {
			opts.keys.push(alertsKeys.bySite(alert.site.id));
		}

		opts.otherScopeKeys.push(alertsKeys.byId(alert.id));

		invalidate(opts);
	};
}

function useUpdateCacheById() {
	const update = useScopeDataUpdater();

	return (alert: Alert) => {
		update(alertsKeys.byId(alert.id), alert);
	};
}

function useAlertCacheEffects() {
	return composeCacheEffects(useInvalidateSiteAlerts(), useUpdateCacheById());
}

// --- Queries ---

export function useSiteAlerts(siteId: MaybeRefOrGetter<string>) {
	const api = useApi().alert();

	const { data: alerts, ...rest } = useScopedQuery({
		key: () => alertsKeys.bySite(toValue(siteId)),
		query: () => api.fetchSiteAlerts(toValue(siteId)),
		enabled: useQueryEnabled(siteId),
	});

	return {
		alerts,
		data: alerts,
		...rest,
	};
}

export function useAlert(id: MaybeRefOrGetter<string>) {
	const api = useApi().alert();

	const { data: alert, ...rest } = useScopedQuery({
		key: () => alertsKeys.byId(toValue(id)),
		query: () => api.fetch(toValue(id)),
		enabled: useQueryEnabled(id),
	});

	return {
		alert,
		data: alert,
		...rest,
	};
}

export function useAlertNotifications(
	id: MaybeRefOrGetter<string>,
	range: MaybeRefOrGetter<[Date, Date]>
) {
	const api = useApi().alert();

	const { data: notifications, ...rest } = useScopedQuery({
		key: () => alertsKeys.notifications(toValue(id), toValue(range)),
		query: () => api.getAlertNotifications(toValue(id), toValue(range)),
		enabled: useQueryEnabled(id),
		placeholderData: (p) => p,
	});

	return {
		notifications,
		data: notifications,
		...rest,
	};
}

// --- Mutations ---

export function useCreateAlert() {
	const api = useApi().alert();

	return useMutation({
		mutation: ({ site, alert }: { site: string; alert: CreateAlert }) => api.create(site, alert),
		onSuccess: useAlertCacheEffects(),
	});
}

export function useUpdateAlert() {
	const api = useApi().alert();

	return useMutation({
		mutation: ({ id, data }: { id: string; data: UpdateAlert }) => api.update(id, data),
		onSuccess: useAlertCacheEffects(),
	});
}

export function useDeleteAlert() {
	const api = useApi().alert();

	return useMutation({
		mutation: async (id: string) => {
			const alert = await api.fetch(id);
			await api.delete(unref(id));

			return alert;
		},
		onSuccess: useInvalidateSiteAlerts(),
	});
}
